import React, { Dispatch, SetStateAction, useMemo } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  SignedInvoicesPageDocument,
  SignedInvoicesPageQuery,
} from 'generated/resources/graphql'

type ExpensesPendingClosingPeriod = NonNullable<
  NonNullable<SignedInvoicesPageQuery['expenses']['pendingClosingPeriodsConnection']>['nodes']
>[number]

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import Content from 'modules/expenses/components/_SignedInvoicesContent'
import { getTaxAgencyOrgsByCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import ClosingPeriodFrequencyModal from './ClosingPeriodFrequencyModal'
import ClosingPeriodModal from './ClosingPeriodModal'

interface SignedInvoicesPageProps {}

const Modal = ({
  aeatClosingPeriodFrequency,
  setOpenModal,
  closingPeriodsPending,
  legalEntityId,
}: {
  aeatClosingPeriodFrequency?: string
  closingPeriodsPending?: ExpensesPendingClosingPeriod[]
  legalEntityId: number
  setOpenModal: Dispatch<SetStateAction<boolean>>
}) => {
  return !!aeatClosingPeriodFrequency ? (
    <ClosingPeriodModal
      setOpenModal={setOpenModal}
      legalEntityId={legalEntityId}
      closingPeriodsPending={closingPeriodsPending}
      aeatClosingPeriodFrequency={aeatClosingPeriodFrequency}
    />
  ) : (
    <ClosingPeriodFrequencyModal setOpenModal={setOpenModal} legalEntityId={legalEntityId} />
  )
}

// TODO: SignedInvoices should be refactored and migrated to Finance Invoices
const SignedInvoicesPage = ({ children }: React.PropsWithChildren<SignedInvoicesPageProps>) => {
  const [openModal, setOpenModal] = React.useState(false)
  const { company } = useCurrentSession()
  const mainLegalEntityId: number | undefined = company.legalEntities.find(
    (legalEntity) => legalEntity.main
  )?.id

  const { data, loading, error } = useQuery(SignedInvoicesPageDocument, {
    variables: { companyId: company?.id, legalEntityId: Number(mainLegalEntityId) },
    skip: !mainLegalEntityId,
  })

  const legalEntity = data?.companies.legalEntitiesConnection.nodes[0]
  const aeatClosingPeriodFrequency = data?.expenses.closingPeriodFrequenciesConnection.nodes.filter(
    (closingPeriodFrequency) => closingPeriodFrequency.organization === 'aeat'
  )[0]?.frequency
  const taxAgencyHomologationEnabled = data?.companies.featureAvailabilities[0]?.available
  const closingPeriodsPending = data?.expenses.pendingClosingPeriodsConnection.nodes

  const taxAgencyOrgs = useMemo(() => {
    if (taxAgencyHomologationEnabled && legalEntity) {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      return getTaxAgencyOrgsByCountry(legalEntity.id, legalEntity.country as 'es' | 'fr')
    }
  }, [taxAgencyHomologationEnabled, legalEntity])

  const { navigation } = useNavigation()

  const appliesHomologation = (() => {
    if (taxAgencyOrgs?.aeat?.applies) {
      return taxAgencyOrgs?.aeat?.id
    }

    if (taxAgencyOrgs?.urssaf?.applies) {
      return taxAgencyOrgs?.urssaf.id
    }
  })()

  return (
    <ResponsivePage.Body loading={loading} error={error}>
      <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
        {taxAgencyHomologationEnabled && appliesHomologation && (
          <Button onClick={() => setOpenModal(true)}>
            {i18n.t('expenses.settings.signed_invoices.list.header.button')}
          </Button>
        )}
        <Button.Router
          to={navigation.settings.expenses.signedInvoices.exports.fullPath()}
          type="secondary"
        >
          {i18n.t('expenses.settings.signed_invoices.list.header.export')}
        </Button.Router>
      </Box>
      <Content>{children}</Content>
      {openModal && legalEntity && (
        <Modal
          aeatClosingPeriodFrequency={aeatClosingPeriodFrequency}
          legalEntityId={legalEntity.id}
          setOpenModal={setOpenModal}
          closingPeriodsPending={closingPeriodsPending}
        ></Modal>
      )}
    </ResponsivePage.Body>
  )
}

export default SignedInvoicesPage
